import { Component, effect, EventEmitter, Injector, Input, OnInit, Output, runInInjectionContext, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { FilterSelectItem } from '../../../models/filter-select-item';
import { FieldFilterService } from '../../../services/filters/field-filter.service';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
    selector: 'app-status-select',
    templateUrl: './status-select.component.html',
    styleUrls: ['./status-select.component.scss'],
    imports: [MatFormField, MatLabel, MatSelect, FormsModule, ReactiveFormsModule, MatTooltip, MatSelectTrigger, MatOption, MatError]
})
export class StatusSelectComponent implements OnInit {

  private _disabled = signal(false);

  @Input() set disabled(value: boolean) {
    this._disabled.set(value);
  }

  @Input() form: FormGroup;
  @Input() color = 'primary';
  @Input() appearance = 'outline';
  @Input() floatLabel = 'always';
  @Input() label = 'Status';
  @Input() placeholder = '';
  @Input() multiple = true;
  @Input() controlName = 'status';
  @Input() toolTipPosition = 'above';
  @Input() selectAllEnabled = true;
  @Input() required = false;
  @Input() origin = "oppty-list";

  options: FilterSelectItem[] = [];
  filteredOptions: FilterSelectItem[] = [];
  stateOptionIds: number[] = [];
  allOption: FilterSelectItem = {ID: 0, Description: 'All', LinkAssociations: [], Selected: false};
  control = new FormControl<any>(null);

  @Output() ready = new EventEmitter();
  @Output() valueChanged = new EventEmitter();

  @ViewChild(MatSelect) select: MatSelect;

constructor(private filterService: FieldFilterService, private injector: Injector) {
  }

  ngOnInit(): void {

    if(this.required) {
      this.control.setValidators([Validators.required]);
    }

    this.form?.addControl(this.controlName, this.control);

    runInInjectionContext(this.injector, () => {
      effect(() => {
        if(this._disabled()) {
          this.control?.disable({ emitEvent: false });
        } else {
          this.control?.enable({ emitEvent: false });
        }
      })});
  }

  getOptionData(stateOptionIds: number[], isEndUserSelect: number = 1) {

    this.stateOptionIds = stateOptionIds;
    const eventIDs = this.getEventTypeIDs();
    this.filterService.getStatusOptions(eventIDs, stateOptionIds, isEndUserSelect).subscribe(result => {
      const items = result?.sort((a, b) => {
        return a.Description.toLowerCase().localeCompare(b.Description.toLowerCase());
      });

      this.options = items;
      this.filteredOptions = items;

      this.ready.emit(this.options);
    });
  }

  setValues(values: FilterSelectItem[], currentStateIds?: number[]) {
    if(!values) {
      return;
    }

    this.filterOptionsFromStateIds(currentStateIds);

    if(values.length == 0 && this.multiple) {

      if(this.filteredOptions.length == 1) {
        this.control.setValue([this.filteredOptions[0]]);
        //this.valueChanged.emit([this.filteredOptions[0]]);
      } else {
        this.control.setValue([this.allOption]);
      }

      setTimeout(() => {
        this.onToggleItem(this.select.options.get(0));
      }, 300);
      
      return;
    }

    this.control.setValue(values);
  }

  setSingleModeValue(value: FilterSelectItem) {
    const foundValue = this.filteredOptions.find(i => i.ID == value.ID);

    if(foundValue) {
      this.control.setValue(foundValue);
    }
  }

  getValues() {
    if(this.allOption.Selected) {
      return [this.allOption];
    }

    return this.control?.value;
  }

  filterOptionsFromStateIds(currentStateIds?: number[]) {
    const stateIds = currentStateIds || this.stateOptionIds;
    if(stateIds.length > 0) {
      this.allOption.Selected = false;
      this.filteredOptions = [];

      this.filteredOptions = this.options.filter(o => o.LinkAssociations.some(a => stateIds.some(b => a === b)));
    }
  }

  onToggleItem(item: MatOption) {
    if(!this.multiple) {
      return;
    }
    
    if(item.value == this.allOption) {
      if(item.selected) {
        this.allOption.Selected = true;
        this.select.options.forEach(o => o.select());
        return;
      }

      this.allOption.Selected = false;
      this.select.options.forEach(o => o.deselect());

    } else {
      
      const allItem = this.select.options.get(0);

      if(!item.selected && allItem.selected) {
        this.allOption.Selected = false;
        allItem.deselect();
        return;
      }

      if(item.selected && !allItem.selected) {
        const check = this.checkOptionAllStatus();
        if(check) {
          this.allOption.Selected = true;
          allItem.select();
        }
      }
    }
  }

  onSelectionChange() {
    this.valueChanged.emit(this.control?.value);
  }

  checkOptionAllStatus() {
    let result = true;
    this.select.options.forEach(o => {
      if(o.value != this.allOption && !o.selected) {
        result = false;
      }
    });

    return result;
  }

  getTooltipString() {
    let result = '';
    const options = this.allOption.Selected ? this.control?.value.slice(1) : this.control?.value;

    options.forEach((o, index) => {
      result += o.Description;

      if(index < options.length - 1) {
        result += ', ';
      }
    })
    return result;
  }

  getSelectCount() {
    return this.allOption.Selected ? this.control?.value.slice(1).length : this.control?.value.length;
  }

  getEventTypeIDs() {
    let result = '';

    switch(this.origin) {
      case "oppty-list":
        result = '1';
        break;

      case "dx-list":
        result = '1,2';
        break;

      default:
        break;
    }

    return result;
  }
}
